import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import SectionTabs from '../components/SectionTabs';
import { CalendarDays, FilePlus, Mail, Package } from 'lucide-react';

function MonthlyUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [latestMonth, setLatestMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const progressIntervalRef = useRef(null);

  const fetchLatestMonth = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/calculateAmountBySong/latestMonth`
      );
      setLatestMonth(response.data.latestMonth);
    } catch (error) {
      console.error('최신 월 불러오기 실패:', error);
    }
  };

  useEffect(() => {
    fetchLatestMonth();
  }, []);

  const deleteAll = async () => {
    if (!latestMonth) {
      alert('삭제할 월 정보가 없습니다.');
      return;
    }

    const confirmDelete = window.confirm(
      `(${latestMonth})의 데이터를 모두 삭제하시겠습니까?`
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/calculateAmountBySong`,
        { data: { latestMonth } }
      );
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/calculateAmountBySong/deleteCA`,
        { data: { latestMonth } }
      );
      alert('삭제가 완료되었습니다.');
    } catch (error) {
      console.error('서버 접속 에러:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const fileValueChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href =
      'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/download/MonthlyUpload.xlsx';
    link.download = 'MonthlyUpload.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stopPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const finishUpload = () => {
    stopPolling();
    setIsUploading(false);
    setLoading(false);
  };

  // 서버가 Redis에 남기는 상태(running/done/error/confirm_required)를 폴링.
  // 업로드 자체는 즉시 수락되고 백그라운드로 처리되므로, 완료/오류는 전부 여기서 감지한다.
  const pollProgress = (id, selectedFile) => {
    stopPolling();
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/progress/${id}`
        );
        const val = res.data.progress;
        const status = res.data.status;
        if (val >= 0) setProgress(val);

        if (status?.status === 'done') {
          setProgress(100);
          finishUpload();
          alert(`월정산 등록을 완료하였습니다. (${(status.inserted ?? 0).toLocaleString()}건)`);
          navigate('/ByClientLatestMonth');
          return;
        }
        if (status?.status === 'error') {
          finishUpload();
          setUploadError(status.message || '등록 중 오류가 발생했습니다.');
          return;
        }
        if (status?.status === 'confirm_required') {
          finishUpload();
          const ok = window.confirm(status.message);
          if (ok) uploadFile(selectedFile, true);
          return;
        }

        // 상태 키가 없던 구버전 호환
        if (!status && val === 100) {
          finishUpload();
          alert('월정산 등록을 완료하였습니다.');
          navigate('/ByClientLatestMonth');
          return;
        }
        if (!status && val === -1) {
          finishUpload();
          setUploadError('등록 중 오류가 발생했습니다. 관리자에게 문의해주세요.');
          return;
        }

        // 안전장치: 30분 넘게 응답이 없으면 안내 후 종료 (처리는 서버에서 계속될 수 있음)
        if (Date.now() - startedAt > 30 * 60 * 1000) {
          finishUpload();
          setUploadError(
            '30분 이상 응답이 없습니다. 서버에서 처리가 계속 중일 수 있으니, 새로고침 후 최근 정산월을 확인하고 재업로드 전에 관리자에게 문의해주세요.'
          );
        }
      } catch (err) {
        console.error('진행률 조회 오류:', err);
      }
    }, 1000);
    progressIntervalRef.current = interval;
  };

  const uploadFile = async (selectedFile, isUpdate) => {
    const newUploadId = uuidv4();
    setLoading(true);
    setIsUploading(true);
    setProgress(0);
    setUploadError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('update', isUpdate ? 'true' : 'false');
    formData.append('uploadId', newUploadId);

    try {
      // 서버는 즉시 { accepted: true }만 응답하고 백그라운드 처리 — 결과는 폴링으로 수신
      await axios.post(
        `${process.env.REACT_APP_API_URL}/calculateAmountBySong/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      pollProgress(newUploadId, selectedFile);
    } catch (error) {
      finishUpload();
      const msg =
        error.response?.data?.errorMessage || '업로드 요청에 실패했습니다. 네트워크 상태를 확인해주세요.';
      setUploadError(msg);
    }
  };

  const add = async () => {
    if (!file) {
      alert('파일을 선택해 주세요.');
      return;
    }
    await uploadFile(file, false);
  };

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">음원 월정산 업로드</div>
      </div>

      <SectionTabs items={[
        { label: '음원 월정산', to: '/MonthlyUpload', icon: CalendarDays },
        { label: '음반 월정산', to: '/PhysicalUpload', icon: Package },
        { label: '대량등록', to: '/BigDataUpload', icon: FilePlus },
        { label: '권리사메일발송', to: '/EmailToClients', icon: Mail },
      ]} />

      {/* 업로드 */}
      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#182334] p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-300">엑셀 업로드</h3>
        <input
          type="file"
          accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={fileValueChange}
          className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-500/10 file:text-sky-400 hover:file:bg-sky-500/20 hover:cursor-pointer"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={add}
            disabled={loading || !file}
            className="px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 text-sm font-semibold rounded-xl disabled:opacity-50 transition-colors">
            {loading ? '처리 중...' : '업로드'}
          </button>
          <button
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-300 text-sm font-semibold transition-colors"
            onClick={handleDownload}>
            양식 다운로드
          </button>
        </div>
      </div>

      {/* 삭제 */}
      <div className="mt-4 rounded-2xl border border-white/[0.08] bg-[#182334] p-6 space-y-3">
        <h3 className="text-sm font-semibold text-slate-300">최신 월 데이터 삭제</h3>
        <div className="flex items-center gap-3">
          {latestMonth && (
            <span className="text-sm text-slate-400">{latestMonth}</span>
          )}
          <button
            onClick={deleteAll}
            className="px-4 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 text-sm font-semibold rounded-xl transition-colors">
            {latestMonth ? `${parseInt(latestMonth?.split('-')[1], 10)}월` : ''} 데이터 전체삭제
          </button>
        </div>
      </div>

      {uploadError && (
        <div className="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3">
          <span className="text-rose-400 text-lg mt-0.5">⚠️</span>
          <div>
            <div className="text-sm font-semibold text-rose-400 mb-1">업로드 실패</div>
            <div className="text-xs text-rose-300 whitespace-pre-wrap break-all">{uploadError}</div>
          </div>
          <button onClick={() => setUploadError(null)} className="ml-auto text-rose-400 hover:text-rose-200 text-lg leading-none">✕</button>
        </div>
      )}

      {isUploading && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-[#182334] border border-white/10 rounded-2xl px-8 py-9 w-80 flex flex-col items-center gap-7">

            {/* 3-ring hi-tech spinner */}
            <div className="relative flex items-center justify-center w-24 h-24">
              {/* 외부 링 */}
              <div className="absolute inset-0 rounded-full animate-spin" style={{ animationDuration: '2.6s', background: 'conic-gradient(from 200deg, transparent 38%, #0369a1 72%, #38bdf8 88%, transparent 100%)' }} />
              <div className="absolute rounded-full" style={{ inset: 5, background: '#182334' }} />
              {/* 틱 마크 - 외부 */}
              {[0,60,120,180,240,300].map(deg => (
                <div key={deg} className="absolute w-0.5 h-1.5 rounded-full bg-sky-600/50" style={{ top: 2, left: '50%', transformOrigin: '50% 46px', transform: `translateX(-50%) rotate(${deg}deg)` }} />
              ))}

              {/* 중간 링 */}
              <div className="absolute rounded-full animate-spin" style={{ inset: 11, animationDuration: '1.7s', animationDirection: 'reverse', background: 'conic-gradient(from 40deg, transparent 52%, rgba(125,211,252,0.9) 82%, transparent 100%)' }} />
              <div className="absolute rounded-full" style={{ inset: 18, background: '#182334' }} />

              {/* 내부 링 */}
              <div className="absolute rounded-full animate-spin" style={{ inset: 23, animationDuration: '1.1s', background: 'conic-gradient(from 110deg, transparent 60%, #bae6fd 90%, transparent 100%)' }} />
              <div className="absolute rounded-full" style={{ inset: 30, background: '#182334' }} />

              {/* 중앙 글로우 + 도트 */}
              <div className="absolute rounded-full animate-pulse" style={{ inset: 30, background: 'radial-gradient(circle, rgba(56,189,248,0.45) 0%, transparent 70%)' }} />
              <div className="w-2.5 h-2.5 rounded-full bg-sky-300" />
            </div>

            {/* 진행률 */}
            <div className="w-full space-y-2.5">
              <div className="w-full bg-white/[0.07] rounded-full h-[3px] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${progress}%`, background: 'linear-gradient(to right, #0ea5e9, #38bdf8)' }}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] tracking-widest uppercase text-slate-500">Processing</span>
                <span className="text-xs font-semibold text-sky-400 tabular-nums">{progress}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MonthlyUploadPage;
